import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { listAuditLog, type AuditLogEntry } from '../api/auditLog';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { useT, type TFn, type DictKey } from '../i18n';
import { PageContainer, PageHeader } from '../components/Page';
import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';

// ANX-045 — admin-only страница просмотра audit_log записей.
//
// Фильтр action — select из known-events (значения в audit.event_type.*).
// Pagination — cursor-based, кнопка «Загрузить ещё».

const KNOWN_EVENTS = [
  '',
  'site.created',
  'site.updated',
  'site.deleted',
  'operator.passkey_registered',
  'operator.passkey_deleted',
  'operator.mfa_reset_by_admin',
  'operator.recovery_codes_regenerated',
  'tenant.policy_require_mfa_changed',
  'invitation.created',
  'invitation.accepted',
  'webhook.created',
  'webhook.updated',
  'webhook.deleted',
  'conversation.closed',
  'visitor.erasure_requested',
  'export.created',
  'export.delivered',
  'csp.violation',
] as const;

export function AuditLogPage() {
  const t = useT();
  const session = useAuthStore((s) => s.session);
  const isAdmin = session?.operator.role === 'admin';
  const [action, setAction] = useState<string>('');
  const [pages, setPages] = useState<string[]>(['']);

  // Текущая страница = последний cursor в pages[]
  const cursor = pages[pages.length - 1];
  const { data, isLoading, error } = useQuery({
    queryKey: ['audit_log', action, cursor],
    queryFn: () =>
      listAuditLog(getApiClient(), {
        action: action || undefined,
        cursor: cursor || undefined,
        limit: 50,
      }),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <PageContainer width="narrow">
        <PageHeader title={t('audit.title')} />
        <p className="text-sm text-red-600">{t('audit.adminOnly')}</p>
      </PageContainer>
    );
  }

  function onActionChange(v: string) {
    setAction(v);
    setPages(['']);
  }

  function loadMore() {
    if (data?.nextCursor) {
      setPages((p) => [...p, data.nextCursor]);
    }
  }

  return (
    <PageContainer>
      <PageHeader title={t('audit.title')} />

      <div className="mb-3 flex items-center gap-2">
        <label htmlFor="audit-action-filter" className="text-xs text-slate-500">
          {t('audit.filter.action')}
        </label>
        <select
          id="audit-action-filter"
          value={action}
          onChange={(e) => onActionChange(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
        >
          {KNOWN_EVENTS.map((evt) => (
            <option key={evt} value={evt}>
              {evt === '' ? t('audit.filter.all') : labelForAction(evt, t)}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <SkeletonList rows={6} />}
      {error !== null && error !== undefined && (
        <p className="text-sm text-red-600">{t('audit.loadError')}</p>
      )}
      {data && data.entries.length === 0 && (
        <EmptyState icon={ShieldCheck} title={t('audit.empty')} />
      )}
      {data && data.entries.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t('audit.col.when')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.col.action')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.col.actor')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.col.target')}</th>
                <th className="px-3 py-2 text-left font-medium">{t('audit.col.metadata')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.entries.map((e) => (
                <EntryRow key={e.id} e={e} t={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        {pages.length > 1 && (
          <button
            type="button"
            onClick={() => setPages((p) => p.slice(0, -1))}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50"
          >
            {t('audit.prev')}
          </button>
        )}
        {data?.nextCursor && (
          <button
            type="button"
            onClick={loadMore}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50"
          >
            {t('audit.next')}
          </button>
        )}
      </div>
    </PageContainer>
  );
}

function EntryRow({ e, t }: { e: AuditLogEntry; t: TFn }) {
  const when = new Date(e.createdAt);
  const whenStr = isNaN(when.getTime())
    ? e.createdAt
    : when.toLocaleString();
  return (
    <tr>
      <td className="px-3 py-2 align-top text-xs text-slate-500 whitespace-nowrap">{whenStr}</td>
      <td className="px-3 py-2 align-top">{labelForAction(e.action, t)}</td>
      <td className="px-3 py-2 align-top text-slate-600">
        {e.actorType === 'system' ? (
          <span className="italic text-slate-500">{t('audit.actor.system')}</span>
        ) : e.actorType === 'visitor' ? (
          <span className="italic text-slate-500">{t('audit.actor.visitor')}</span>
        ) : (
          <span className="font-mono text-xs">{e.actorId?.slice(0, 8) ?? '—'}</span>
        )}
      </td>
      <td className="px-3 py-2 align-top text-slate-600">
        {e.targetType && (
          <span className="text-xs">
            {e.targetType}
            {e.targetId && (
              <span className="ml-1 font-mono text-slate-400">{e.targetId.slice(0, 8)}</span>
            )}
          </span>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        {e.metadata !== null && e.metadata !== undefined && (
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500">
              {t('audit.metadata.expand')}
            </summary>
            <pre className="mt-1 overflow-x-auto rounded bg-slate-50 px-2 py-1 text-xs text-slate-700">
              {JSON.stringify(e.metadata, null, 2)}
            </pre>
          </details>
        )}
      </td>
    </tr>
  );
}

function labelForAction(action: string, t: TFn): string {
  // Ищем i18n-ключ audit.event.<action>, fallback — raw event-type.
  const key = `audit.event.${action}` as DictKey;
  const localized = t(key);
  // useT возвращает ключ as-is если не нашли; считаем что "audit.event."-prefix
  // означает «локализации нет, отдаём raw».
  if (localized === key) return action;
  return localized;
}
